import { FORMULA_OPERATIONS as FP } from "../widgets/interfaces/model/enumerations/FormulaOperations";
import { IShapeGuide } from "../widgets/interfaces/model/geometry/ShapeGuide";

/**
 * Within the construction code for each of preset shapes there are predefined
 * guides that the generating application shall maintain for calculation
 * purposes at all times. The necessary guides should have the following values.
 *
 * REF: ECMA-376, Third Edition, Part 1 | Section: 20.1.10.55
 */
export const predefinedGuides: IShapeGuide[] = [

	/**
     * Shape Height('h')
     * This is the variable height of the shape defined in the shape properties.This value is received from the shape
     * transform listed within the < spPr > element.
     * { name: "h", fmla: { operation: "val", operands: [/* shape height from spPr -> xfrm -> ext -> cy* /] } },
     *
     * Shape Width ('w')
     * This is the variable width of the shape defined in the shape properties.This value is received from the shape
     * transform listed within the < spPr > element.
     * { name: "w", fmla: { operation: "val", operands: [/* shape width from spPr -> xfrm -> ext -> cx* /] } },
     *
     * 3/4 of a Circle ('3cd4') - Constant value of "16200000.0"
     * The units here are in 60,000ths of a degree. This is equivalent to 270 degrees.
     * { name: "3cd4", fmla: { operation: "val", operands: [16200000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "3cd4", fmla: { operation: FP.LiteralValue, operands: [270] } },

	/**
     * 3/8 of a Circle ('3cd8') - Constant value of "8100000.0"
     * The units here are in 60,000ths of a degree. This is equivalent to 135 degrees.
     * { name: "3cd8", fmla: { operation: "val", operands: [8100000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "3cd8", fmla: { operation: FP.LiteralValue, operands: [135] } },

	/**
     * 5/8 of a Circle ('5cd8') - Constant value of "13500000.0"
     * The units here are in 60,000ths of a degree. This is equivalent to 225 degrees.
     * { name: "5cd8", fmla: { operation: "val", operands: [13500000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "5cd8", fmla: { operation: FP.LiteralValue, operands: [225] } },

	/**
     * 7/8 of a Circle ('7cd8') - Constant value of "18900000.0"
     * The units here are in 60, 000ths of a degree.This is equivalent to 315 degrees.
     * { name: "7cd8", fmla: { operation: "val", operands: [18900000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "7cd8", fmla: { operation: FP.LiteralValue, operands: [315] } },

	/**
     * Shape Bottom Edge ('b') - Constant value of "h"
     * This is the bottom edge of the shape and since the top edge of the shape is considered the 0 point, the
     * bottom edge is thus the shape height.
     */
	{ name: "b", fmla: { operation: FP.LiteralValue, operands: ["h"] } },

	/**
     * 1/2 of a Circle ('cd2') - Constant value of "10800000.0"
     * The units here are in 60,000ths of a degree. This is equivalent to 180 degrees.
     * { name: "cd2", fmla: { operation: "val", operands: [10800000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "cd2", fmla: { operation: FP.LiteralValue, operands: [180] } },

	/**
     *  1/4 of a Circle ('cd4') - Constant value of "5400000.0"
     * The units here are in 60,000ths of a degree. This is equivalent to 90 degrees.
     * { name: "cd4", fmla: { operation: "val", operands: [5400000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "cd4", fmla: { operation: FP.LiteralValue, operands: [90] } },

	/**
     * 1/8 of a Circle ('cd8') - Constant value of "2700000.0"
     * The units here are in 60,000ths of a degree. This is equivalent to 45 degrees.
     * { name: "cd8", fmla: { operation: "val", operands: [2700000] } },
     * Instead of microsoft version of degree, stick to normal degrees
     */
	{ name: "cd8", fmla: { operation: FP.LiteralValue, operands: [45] } },

	/**
     * Horizontal Center('hc') - Calculated value of "* / w 1.0 2.0"
     * This is the horizontal center of the shape which is just the width divided by 2.
     */
	{ name: "hc", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 2] } },

	/**
     * 1/2 of Shape Height ('hd2') - Calculated value of "* / h 1.0 2.0"
     * This is 1 / 2 the shape height.
     */
	{ name: "hd2", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 2] } },

	/**
     * 1/3 of Shape Height ('hd3') - Calculated value of "* / h 1.0 3.0"
     * This is 1 / 3 the shape height.
     */
	{ name: "hd3", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 3] } },

	/**
     * 1/4 of Shape Height ('hd4') - Calculated value of "* / h 1.0 4.0"
     * This is 1 / 4 the shape height.
     */
	{ name: "hd4", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 4] } },

	/**
     * 1/5 of Shape Height ('hd5') - Calculated value of "* / h 1.0 5.0"
     * This is 1 / 5 the shape height.
     */
	{ name: "hd5", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 5] } },

	/**
     * 1/6 of Shape Height ('hd6') - Calculated value of "* / h 1.0 6.0"
     * This is 1 / 6 the shape height.
     */
	{ name: "hd6", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 6] } },

	/**
     * 1/8 of Shape Height ('hd8') - Calculated value of "* / h 1.0 8.0"
     * This is 1 / 8 the shape height.
     */
	{ name: "hd8", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 8] } },

	/**
     * Shape Left Edge ('l') - Constant value of "0"
     * This is the left edge of the shape and the left edge of the shape is considered the horizontal 0 point.
     */
	{ name: "l", fmla: { operation: FP.LiteralValue, operands: [0] } },

	/**
     * Longest Side of Shape ('ls') - Calculated value of "max w h"
     * This is the longest side of the shape.This value is either the width or the height depending on which is greater.
     */
	{ name: "ls", fmla: { operation: FP.MaximumValue, operands: ["w", "h"] } },

	/**
     * Shape Right Edge ('r') - Constant value of "w"
     * This is the right edge of the shape and since the left edge of the shape is considered the 0 point, the right
     * edge is thus the shape width.
     */
	{ name: "r", fmla: { operation: FP.LiteralValue, operands: ["w"] } },

	/**
     * Shortest Side of Shape ('ss') - Calculated value of "min w h"
     * This is the shortest side of the shape.This value is either the width or the height depending on which is
     * smaller.
     */
	{ name: "ss", fmla: { operation: FP.MinimumValue, operands: ["w", "h"] } },

	/**
     * 1/2 Shortest Side of Shape ('ssd2') - Calculated value of "* / ss 1.0 2.0"
     * This is 1 / 2 the shortest side of the shape.
     */
	{ name: "ssd2", fmla: { operation: FP.MultiplyDivide, operands: ["ss", 1, 2] } },

	/**
     * 1/4 Shortest Side of Shape ('ssd4') - Calculated value of "* / ss 1.0 4.0"
     * This is 1 / 4 the shortest side of the shape.
     */
	{ name: "ssd4", fmla: { operation: FP.MultiplyDivide, operands: ["ss", 1, 4] } },

	/**
     * 1/6 Shortest Side of Shape ('ssd6') - Calculated value of "* / ss 1.0 6.0"
     * This is 1 / 6 the shortest side of the shape.
     */
	{ name: "ssd6", fmla: { operation: FP.MultiplyDivide, operands: ["ss", 1, 6] } },

	/**
     * 1/8 Shortest Side of Shape ('ssd8') - Calculated value of "* / ss 1.0 8.0"
     * This is 1 / 8 the shortest side of the shape.
     */
	{ name: "ssd8", fmla: { operation: FP.MultiplyDivide, operands: ["ss", 1, 8] } },

	/**
     * Shape Top Edge ('t') - Constant value of "0"
     * This is the top edge of the shape and the top edge of the shape is considered the vertical 0 point.
     */
	{ name: "t", fmla: { operation: FP.LiteralValue, operands: [0] } },

	/**
     * Vertical Center of Shape ('vc') - Calculated value of "* / h 1.0 2.0"
     * This is the vertical center of the shape which is just the height divided by 2.
     */
	{ name: "vc", fmla: { operation: FP.MultiplyDivide, operands: ["h", 1, 2] } },

	/**
     * 1/2 of Shape Width ('wd2') - Calculated value of "* / w 1.0 2.0"
     * This is 1 / 2 the shape width.
     */
	{ name: "wd2", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 2] } },

	/**
     *  /4 of Shape Width ('wd4') - Calculated value of "* / w 1.0 4.0"
     * This is 1 / 4 the shape width.
     */
	{ name: "wd4", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 4] } },

	/**
     * 1/5 of Shape Width ('wd5') - Calculated value of "* / w 1.0 5.0"
     * This is 1 / 5 the shape width.
     */
	{ name: "wd5", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 5] } },

	/**
     * 1/6 of Shape Width ('wd6') - Calculated value of "* / w 1.0 6.0"
     * This is 1 / 6 the shape width.
     */
	{ name: "wd6", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 6] } },

	/**
     * 1/8 of Shape Width ('wd8') - Calculated value of "* / w 1.0 8.0"
     * This is 1/8 the shape width.
     */
	{ name: "wd8", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 8] } },

	/**
     * 1/10 of Shape Width ('wd10') - Calculated value of "* / w 1.0 10.0"
     * This is 1 / 10 the shape width.
     */
	{ name: "wd10", fmla: { operation: FP.MultiplyDivide, operands: ["w", 1, 10] } },

];
